import { useRef, useState } from 'react';
import { useDebounce } from '../../../hooks';

/**
 * The DataTable's server-side search for rows it has not loaded.
 *
 * A table that loads a row's children when the row is opened cannot filter on them: a row that was
 * never read is not in the browser to be matched. This asks the consumer to search for them
 * instead, and hands back the matches for the table to merge in as rows of its own.
 *
 * The hook owns the request lifecycle and nothing else — what to search and how is the consumer's
 * `childFilter.fetch`, and what to do with the matches is the table's.
 *
 * @param {object} args
 * @param {{fetch: Function, top?: number}} [args.childFilter] - The consumer's search, as documented
 *   on the DataTable's childFilter prop. Without one the hook is inert.
 * @param {number} args.defaultTop - Bound on the number of matches to ask for, when the config
 *   names none.
 * @returns {{
 *   childMatches: object[]|undefined,
 *   childMatchesTruncated: boolean,
 *   loadingChildMatches: boolean,
 *   requestChildMatches: (term: string, fields: string[]) => void,
 * }} `childMatches` is undefined while no search is active, which is what tells the table to leave
 *   its rows alone.
 */
export const useChildFilter = ({ childFilter, defaultTop }) => {
  // Rows matching the current filter that the table has not loaded, or undefined when no search is
  // active.
  const [childMatches, setChildMatches] = useState();
  const [childMatchesTruncated, setChildMatchesTruncated] = useState(false);
  const [loadingChildMatches, setLoadingChildMatches] = useState(false);

  // The debounced search runs outside the render that scheduled it, so it reads the current config
  // from a ref rather than closing over a stale prop.
  const childFilterRef = useRef(childFilter);
  childFilterRef.current = childFilter;
  const abortRef = useRef();

  /**
   * Runs the consumer's search and keeps the result. Debounced on the same 200 ms as the in-browser
   * filter, and single-flight: a new keystroke burst aborts the request in flight, and a response
   * that arrives after its request was superseded is discarded rather than applied.
   * @param {string} term Filter term to search for
   * @param {string[]} fields Columns to search, from the filter's column selector
   */
  const fetchChildMatches = useDebounce((term, fields) => {
    const config = childFilterRef.current;
    if (!config?.fetch) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const top = config.top || defaultTop;
    setLoadingChildMatches(true);

    config
      .fetch(term, { signal: controller.signal, top, fields })
      .then(matches => {
        if (abortRef.current !== controller) return;

        setChildMatches(matches || []);
        // A full page of matches means the server stopped counting, not that there were exactly
        // that many. The table says so rather than pretending the list is complete.
        setChildMatchesTruncated((matches || []).length >= top);
      })
      // The consumer's fetch reports its own errors; an aborted request is not one. Either way the
      // stale matches go, so the table never shows a row found for a term that is no longer active.
      .catch(() => {
        if (abortRef.current === controller) setChildMatches(undefined);
      })
      .finally(() => {
        if (abortRef.current === controller) setLoadingChildMatches(false);
      });
  }, 200).debounce;

  /**
   * Drops the search and everything it produced.
   */
  const clearChildMatches = () => {
    fetchChildMatches.cancel();
    abortRef.current?.abort();
    abortRef.current = undefined;
    setChildMatches(undefined);
    setChildMatchesTruncated(false);
    setLoadingChildMatches(false);
  };

  /**
   * Starts or drops the search, according to whether there is anything to search: a term long
   * enough to filter on, and at least one column ticked in the column selector. Searching a column
   * the user has unticked would return rows the table then filters straight out again.
   *
   * @param {string} term Filter term
   * @param {string[]} fields Ticked columns
   */
  const requestChildMatches = (term, fields) => {
    (term || '').length > 1 && (fields || []).length > 0
      ? fetchChildMatches(term, fields)
      : clearChildMatches();
  };

  return {
    childMatches,
    childMatchesTruncated,
    loadingChildMatches,
    requestChildMatches,
  };
};
